import { TextCell } from "./TextCell";
import { CheckboxBodyCell } from "./CheckboxBodyCell";
import { Stack } from "@mui/material";
import { RoleMembersItem } from "types/apiTypes";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { SeniorityBodyCell } from "./SeniorityBodyCell";
import { DataSourceBodyCell } from "./DataSourceBodyCell";
import { DATA_TYPES } from "../../../constant";
import { MemberMenu } from "../../ui";
import { useAppSelector } from "../../../hooks";

type Status = "committee_status" | "cluster_status";

const getIsSelected = (status: string, check: string, isEndCheck?: boolean) => {
  if (!status) return false;
  return isEndCheck ? status.endsWith(check) : status.startsWith(check);
};

type Props = RoleMembersItem & {
  isEditMode: boolean;
  updatedValues?: MembersUpdateValues;
  updateHandler: (values: MembersUpdateValues) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
};

const INCLUDED = "included";
const MANUALLY = "manually";

export const RoleItemMember = (props: Props) => {
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const {
    data_source,
    cluster_status,
    committee_status,
    title,
    id,
    linkedin_updated_at,
    warning_tooltip,
    name,
    seniority,
    is_edited,
    updateHandler,
    updatedValues,
    isEditMode,
    setCommitteeSelected,
    setClusterSelected,
    is_hidden,
    cluster,
    committee,
    engagement,
    sdr_playbook_status,
    is_activated,
  } = props;

  const isSelected =
    getIsSelected(cluster_status, INCLUDED, true) ||
    getIsSelected(committee_status, INCLUDED, true);

  const isEdited =
    getIsSelected(cluster_status, MANUALLY) ||
    getIsSelected(committee_status, MANUALLY) ||
    !!updatedValues;

  const isCommitteSelected =
    updatedValues?.committee === undefined
      ? getIsSelected(committee_status, INCLUDED, true)
      : updatedValues.committee;

  const isClusterSelected =
    updatedValues?.cluster === undefined
      ? getIsSelected(cluster_status, INCLUDED, true)
      : updatedValues.cluster;

  const selectedHandler = (type: Status) => {
    if (type === "committee_status") {
      const initCommittee = getIsSelected(committee_status, INCLUDED, true);
      const initCluster = getIsSelected(cluster_status, INCLUDED, true);
      const isSame = initCommittee === !isCommitteSelected;

      const committee = isSame ? undefined : !isCommitteSelected;
      const cluster =
        isCommitteSelected && isClusterSelected
          ? !initCluster
            ? undefined
            : false
          : isClusterSelected;

      setCommitteeSelected((prev) =>
        !isCommitteSelected ? prev + 1 : prev - 1,
      );

      isClusterSelected &&
        isCommitteSelected &&
        setClusterSelected((prev) => prev - 1);

      updateHandler({
        member_id: id,
        committee,
        cluster,
      });
    } else {
      const initIsSelected = getIsSelected(cluster_status, INCLUDED, true);
      const isSame = initIsSelected === !isClusterSelected;

      setClusterSelected((prev) => (!isClusterSelected ? prev + 1 : prev - 1));

      updateHandler({
        member_id: id,
        cluster: isSame ? undefined : !isClusterSelected,
      });
    }
  };

  return (
    <Stack
      data-id={id}
      className={isEditMode ? undefined : "no-drag"}
      sx={{
        flexGrow: 1,
        flexDirection: "row",
        alignItems: "center",
        padding: "14px 0",
        borderRadius: "8px",
        cursor: isEditMode ? "grab" : "auto",
        backgroundColor: is_activated ? "#F3FAF5" : "transparent",
        border: `1px solid ${is_activated ? "#27AE60" : "#E0E0E0"}`,
      }}
    >
      <DataSourceBodyCell
        is_hidden={is_hidden}
        isHighlighted={isSelected}
        data_source={
          data_source === "user_crm" ? DATA_TYPES.user : DATA_TYPES.infuse
        }
        value={name}
        maxWidth={"48%"}
        minWidth={"120px"}
        warning_tooltip={warning_tooltip}
        linkedin_updated_at={linkedin_updated_at}
        title={title}
        isRoleEdit={isEditMode}
        is_activated={is_activated}
      />
      <SeniorityBodyCell
        value={seniority}
        maxWidth={"20%"}
        minWidth={"100px"}
        isEdited={isEdited || is_edited}
      />
      <TextCell
        indicator={engagement.indicator}
        indicatorChange={engagement.change}
        indicatorChangeAt={engagement.changed_at}
        isHighlighted={isSelected}
        value={`${engagement.score === null ? 0 : engagement.score}%`}
        maxWidth={"12%"}
        minWidth={"124px"}
        justifyContent="flex-end"
      />
      <CheckboxBodyCell
        indicator={committee.indicator}
        indicatorChange={committee.change}
        indicatorChangeAt={committee.changed_at}
        isHighlighted={isSelected}
        isSelected={isCommitteSelected}
        isEditMode={isEditMode}
        value={`${committee.score === null ? 0 : committee.score}%`}
        maxWidth={"12%"}
        minWidth={"144px"}
        justifyContent="flex-end"
        onChangeHandler={() => selectedHandler("committee_status")}
      />
      <CheckboxBodyCell
        indicator={cluster.indicator}
        indicatorChange={cluster.change}
        indicatorChangeAt={cluster.changed_at}
        isHighlighted={isSelected}
        isSelected={isClusterSelected}
        isDisabled={!isCommitteSelected}
        isEditMode={isEditMode}
        value={`${cluster.score === null ? 0 : cluster.score}%`}
        maxWidth={"12%"}
        minWidth={"104px"}
        justifyContent="flex-end"
        onChangeHandler={() => selectedHandler("cluster_status")}
      />
      {isEditor && (
        <MemberMenu
          member_id={id}
          isDisabled={false}
          playbookStatus={sdr_playbook_status}
        />
      )}
    </Stack>
  );
};
